import React, { useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import {
  X, User, Phone, Mail, MapPin,
  ChevronDown, Check, Loader2,
  ArrowRight, Droplets, Home, MessageSquare
} from "lucide-react";
import wp1 from "../assets/waterproofing10.jpg"; // Path check kar lena

// CHANGE: 'setIsConsultOpen' ko yahan se hata diya gaya hai kyunki iski zaroorat nahi thi
interface BookConsultModalProps {
  isOpen: boolean;
  onClose: () => void;
}

const BookConsultModal: React.FC<BookConsultModalProps> = ({ isOpen, onClose }) => {
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitStatus, setSubmitStatus] = useState<"idle" | "success">("idle");

  const [formData, setFormData] = useState({
    name: "",
    phone: "",
    email: "",
    city: "",
    propertyType: "",
    leakageType: "",
    urgency: "",
    otherLeakage: "",
    whatsapp: "",
    sameAsPhone: true
  });

  const handleChange = (e: any) => {
    const { name, value } = e.target;
    setFormData((prev) => ({ ...prev, [name]: value }));
  };

  const handleSubmit = (e: any) => {
    e.preventDefault();
    setIsSubmitting(true);
    const finalWhatsapp = formData.sameAsPhone
      ? formData.phone
      : formData.whatsapp;

    console.log("Submitting:", { ...formData, finalWhatsapp });
    
    // Simulate API call
    setTimeout(() => {
      setIsSubmitting(false);
      setSubmitStatus("success");
    }, 1500);
  };

  const toggleWhatsapp = () => {
    setFormData((prev) => ({
      ...prev,
      sameAsPhone: !prev.sameAsPhone,
      whatsapp: !prev.sameAsPhone ? "" : prev.phone
    }));
  };

  if (!isOpen) return null;

  return (
    <AnimatePresence>
      <div className="fixed inset-0 z-[9999] flex items-center justify-center bg-stone-900/80 backdrop-blur-sm p-4">
        <motion.div
          initial={{ scale: 0.95, opacity: 0 }}
          animate={{ scale: 1, opacity: 1 }}
          exit={{ scale: 0.95, opacity: 0 }}
          transition={{ type: "spring", damping: 25, stiffness: 300 }}
          className="bg-white rounded-2xl w-full max-w-4xl flex overflow-hidden shadow-2xl relative max-h-[90vh] flex-col md:flex-row"
        >
          {/* Close Button */}
          <button
            onClick={onClose}
            className="absolute top-3 right-3 z-50 bg-gray-100 hover:bg-red-50 p-2 rounded-full transition-colors shadow-sm cursor-pointer"
          >
            <X size={20} className="text-gray-500 hover:text-red-500" />
          </button>

          {/* LEFT: Image Section */}
          <div className="hidden md:flex w-5/12 relative bg-stone-900">
            <img
              src={wp1}
              alt="Waterproofing"
              className="absolute inset-0 w-full h-full object-cover opacity-60"
            />
            <div className="absolute inset-0 bg-gradient-to-t from-stone-900 via-stone-900/40 to-transparent" />
            <div className="absolute bottom-10 left-8 text-white z-10">
              <div className="w-12 h-1 bg-amber-500 mb-4 rounded-full"></div>
              <h3 className="text-3xl font-serif font-bold leading-tight">
                Premium <br /> Protection
              </h3>
              <p className="text-stone-300 mt-2 text-sm max-w-[80%]">
                Expert waterproofing services designed to last a lifetime.
              </p>
            </div>
          </div>

          {/* RIGHT: Form Section */}
          <div className="w-full md:w-7/12 p-5 md:p-8 flex flex-col justify-center bg-white relative overflow-y-auto">
            <Droplets className="absolute top-6 left-6 text-blue-50 w-32 h-32 -z-0 opacity-50 rotate-12" />

            {submitStatus === "success" ? (
              <SuccessView onClose={onClose} />
            ) : (
              <div className="relative z-10">
                <div className="mb-5 text-center md:text-left pt-2">
                  <span className="text-[10px] font-bold text-amber-600 tracking-[0.2em] uppercase border border-amber-200 px-2 py-1 rounded-sm bg-amber-50">
                    Free Inspection
                  </span>
                  <h1 className="text-2xl md:text-3xl font-serif font-bold text-stone-900 mt-2">
                    Book Expert Visit
                  </h1>
                </div>

                <form onSubmit={handleSubmit} className="space-y-3">
                  <Input icon={<User />} name="name" value={formData.name} onChange={handleChange} placeholder="Full Name" fullWidth />
                  <div className="grid grid-cols-2 gap-3">
                    <Input icon={<Phone />} name="phone" value={formData.phone} onChange={handleChange} placeholder="Phone No." type="tel" />
                    <Input icon={<Mail />} name="email" value={formData.email} onChange={handleChange} placeholder="Email" type="email" />
                  </div>

                  <div className="bg-stone-50 p-2.5 rounded-lg border border-stone-100">
                    <div className="flex items-center justify-between">
                      <div className="flex items-center gap-2 cursor-pointer" onClick={toggleWhatsapp}>
                         <div className={`w-4 h-4 rounded border flex items-center justify-center transition-colors ${formData.sameAsPhone ? 'bg-green-500 border-green-500' : 'border-gray-400 bg-white'}`}>
                            {formData.sameAsPhone && <Check size={12} className="text-white" />}
                         </div>
                         <span className="text-xs text-stone-600 font-medium select-none">WhatsApp is same as Phone</span>
                      </div>
                    </div>
                    <AnimatePresence>
                      {!formData.sameAsPhone && (
                        <motion.div initial={{ height: 0, opacity: 0, marginTop: 0 }} animate={{ height: "auto", opacity: 1, marginTop: 10 }} exit={{ height: 0, opacity: 0, marginTop: 0 }} className="overflow-hidden">
                          <Input icon={<MessageSquare />} name="whatsapp" value={formData.whatsapp} onChange={handleChange} placeholder="WhatsApp Number" className="bg-white" />
                        </motion.div>
                      )}
                    </AnimatePresence>
                  </div>

                  <div className="grid grid-cols-2 gap-3">
                    <Input icon={<MapPin />} name="city" value={formData.city} onChange={handleChange} placeholder="City" />
                     <Select icon={<Droplets />} name="leakageType" value={formData.leakageType} onChange={handleChange} options={["Roof", "Bathroom", "Wall", "Basement", "Other"]} placeholder="Leakage" />
                  </div>

                  <div className="grid grid-cols-2 gap-3">
                     <Select icon={<Home />} name="propertyType" value={formData.propertyType} onChange={handleChange} options={["Flat", "House", "Villa", "Office"]} placeholder="Property" />
                    <Select icon={<ChevronDown />} name="urgency" value={formData.urgency} onChange={handleChange} options={["Immediate", "This Week", "Just Enquiry"]} placeholder="Urgency" />
                  </div>

                  <button type="submit" disabled={isSubmitting} className="w-full mt-1 bg-gradient-to-r from-stone-900 to-stone-800 hover:from-amber-700 hover:to-amber-600 text-white py-3 rounded-lg font-bold text-sm uppercase tracking-widest flex items-center justify-center gap-2 transition-all shadow-lg hover:shadow-amber-500/20 active:scale-[0.98]">
                    {isSubmitting ? <Loader2 className="w-5 h-5 animate-spin" /> : <>Book Inspection <ArrowRight className="w-4 h-4" /></>}
                  </button>
                </form>
              </div>
            )}
          </div>
        </motion.div>
      </div>
    </AnimatePresence>
  );
};

// Sub-components as they were...
const SuccessView = ({ onClose }: { onClose: () => void }) => (
  <motion.div initial={{ opacity: 0, scale: 0.9 }} animate={{ opacity: 1, scale: 1 }} className="flex flex-col items-center justify-center text-center h-full py-10">
    <div className="w-20 h-20 bg-green-50 rounded-full flex items-center justify-center mb-6 ring-4 ring-green-100"><Check className="w-10 h-10 text-green-600" /></div>
    <h2 className="text-3xl font-serif font-bold text-stone-900 mb-2">Request Sent!</h2>
    <p className="text-stone-500 max-w-xs mx-auto mb-8">Our waterproofing expert will analyze your request and call you shortly.</p>
    <button onClick={onClose} className="text-sm font-bold text-stone-400 hover:text-stone-900 uppercase tracking-widest">Close Window</button>
  </motion.div>
);

const Input = ({ icon, fullWidth, className = "", ...props }: any) => (
  <div className={`relative group ${fullWidth ? "col-span-2" : "col-span-1"}`}>
    <div className="absolute left-3 top-1/2 -translate-y-1/2 text-stone-400 group-focus-within:text-amber-600 transition-colors">{React.cloneElement(icon, { size: 16 })}</div>
    <input {...props} className={`w-full bg-stone-50/50 border border-stone-200 rounded-lg py-2.5 pl-10 pr-3 text-sm text-stone-800 placeholder:text-stone-400 focus:outline-none focus:border-amber-500 focus:ring-1 focus:ring-amber-500 transition-all ${className}`} />
  </div>
);

const Select = ({ icon, options, placeholder, ...props }: any) => (
  <div className="relative group">
    <div className="absolute left-3 top-1/2 -translate-y-1/2 text-stone-400 group-focus-within:text-amber-600 transition-colors">{React.cloneElement(icon, { size: 16 })}</div>
    <select {...props} required className="w-full appearance-none bg-stone-50/50 border border-stone-200 rounded-lg py-2.5 pl-10 pr-8 text-sm text-stone-800 focus:outline-none focus:border-amber-500 focus:ring-1 focus:ring-amber-500 transition-all cursor-pointer">
      <option value="" disabled selected>{placeholder}</option>
      {options.map((o: string) => <option key={o} value={o}>{o}</option>)}
    </select>
    <ChevronDown className="absolute right-3 top-1/2 -translate-y-1/2 w-4 h-4 text-stone-300 pointer-events-none" />
  </div>
);

export default BookConsultModal;